import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { Flex, Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { BsFillInfoCircleFill, FiTrash } from 'react-icons/all';
import { MultiSelect } from 'react-multi-select-component';
import { FiEdit } from 'react-icons/fi';
import { ca } from 'date-fns/locale';
import { Input, Table } from '~/components';
import { Dashboard, SelectItem } from '~/types';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import { getGroupsAsSelect } from '~/hooks';
import { queryClient } from '~/utils/react-query';
import api from '~/utils/api';

export const DashboardForm: React.FC<{
  isEdit: boolean;
  dashboard?: Dashboard;
}> = ({ isEdit, dashboard }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const [isFetching, setFetching] = useState(false);
  const { errors } = formState;
  const [nomeRelatorio, setNomeRelatorio] = useState<string>('');
  const [urlApi, setUrlApi] = useState<string>('');
  const [nomeTabelaCliente, setNomeTabelaCliente] = useState<string>('');
  const [campoCodigoCliente, setCampoCodigoCliente] = useState<string>('');
  const [nomeTabelaGrupoEconomico, setNomeTabelaGrupoEconomico] =
    useState<string>('');
  const [campoCodigoGrupoEconomico, setCampoCodigoGrupoEconomico] =
    useState<string>('');
  const [tenantId, setTenantId] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [clientesSelecionados, setClientesSelecionados] = useState<
    SelectItem[]
  >([]);
  const [clientes, setClientes] = useState<SelectItem[]>([]);

  const [
    nomeCampoGrupoEconomicoObrigatorio,
    setNomeCampoGrupoEconomicoObrigatorio,
  ] = useState<boolean>(false);

  const handleOpen = () => {
    setFetching(true);
    setClientesSelecionados([]);
    getGroupsAsSelect(9999)
      .then(_groups => {
        setClientes(_groups);
        if (isEdit && dashboard) {
          setNomeRelatorio(dashboard.nome_relatorio);
          setUrlApi(dashboard.url_api);
          setNomeTabelaCliente(dashboard.nome_tabela_cliente);
          setCampoCodigoCliente(dashboard.campo_codigo_cliente);
          setNomeTabelaGrupoEconomico(dashboard.nome_tabela_grupo_economico);
          setCampoCodigoGrupoEconomico(dashboard.campo_codigo_grupo_economico);
          setTenantId(dashboard.tenant_id);
          setClientId(dashboard.client_id);
          setClientSecret(dashboard.client_secret);
          setClientesSelecionados(() => {
            return _groups?.filter(item =>
              dashboard.clientes
                .map(client => client.id_grupoeconomico)
                .includes(Number(item.value)),
            );
          });
          !!dashboard.nome_tabela_grupo_economico ||
          !!dashboard.nome_tabela_grupo_economico
            ? setNomeCampoGrupoEconomicoObrigatorio(true)
            : setNomeCampoGrupoEconomicoObrigatorio(false);
        }
      })
      .finally(() => {
        setFetching(false);
      });
    onOpen();
  };
  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setNomeRelatorio('');
    setUrlApi('');
    setNomeTabelaCliente('');
    setCampoCodigoCliente('');
    setNomeTabelaGrupoEconomico('');
    setCampoCodigoGrupoEconomico('');
    clearErrors();
  };
  const isValidUrl = (str: string) => {
    const regex =
      /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%\-/]))?/;
    return regex.test(str);
  };
  async function handleCreate() {
    clearErrors();

    (!nomeRelatorio || !nomeRelatorio.trim().length) &&
      setError('nomeRelatorio', {
        type: 'required',
        message: 'Nome do relatório é obrigatório',
      });
    (!urlApi || !urlApi.trim().length) &&
      setError('urlApi', {
        type: 'required',
        message: 'URL da API é obrigatório',
      });
    !!urlApi &&
      !isValidUrl(urlApi) &&
      setError('urlApi', {
        type: 'required',
        message: 'URL da API é inválida',
      });
    (!nomeTabelaCliente || !nomeTabelaCliente.trim().length) &&
      setError('nomeTabelaCliente', {
        type: 'required',
        message: 'Nome da Tabela Cliente é obrigatório',
      });
    (!campoCodigoCliente || !campoCodigoCliente.trim().length) &&
      setError('campoCodigoCliente', {
        type: 'required',
        message: 'Campo do Código Cliente é obrigatório',
      });
    nomeCampoGrupoEconomicoObrigatorio &&
      (!campoCodigoGrupoEconomico ||
        !campoCodigoGrupoEconomico.trim().length) &&
      setError('campoCodigoGrupoEconomico', {
        type: 'required',
        message: 'Campo do Código Grupo Econômico é obrigatório',
      });
    nomeCampoGrupoEconomicoObrigatorio &&
      (!nomeTabelaGrupoEconomico || !nomeTabelaGrupoEconomico.trim().length) &&
      setError('nomeTabelaGrupoEconomico', {
        type: 'required',
        message: 'Nome da Tabela Grupo Econômico é obrigatório',
      });
    (!tenantId || !tenantId.trim().length) &&
      setError('tenantId', {
        type: 'required',
        message: 'TENANT_ID é obrigatório',
      });
    (!clientId || !clientId.trim().length) &&
      setError('clientId', {
        type: 'required',
        message: 'CLIENT_ID é obrigatório',
      });
    (!clientSecret || !clientSecret.trim().length) &&
      setError('clientSecret', {
        type: 'required',
        message: 'CLIENT_SECRET é obrigatório',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const data = {
      nome_relatorio: nomeRelatorio,
      url_api: urlApi,
      nome_tabela_cliente: nomeTabelaCliente,
      campo_codigo_cliente: campoCodigoCliente,
      nome_tabela_grupo_economico: nomeTabelaGrupoEconomico.trim() || null,
      campo_codigo_grupo_economico: campoCodigoGrupoEconomico.trim() || null,
      clientes: clientesSelecionados.map(c => Number(c.value)),
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
    } as unknown as Dashboard;

    setFetching(true);

    let request;
    if (!isEdit) {
      request = api.post(`dashboards`, data);
    } else {
      request = api.put(`dashboards/${dashboard?.id}`, data);
    }

    request
      .then(() => {
        toast({
          title: `URL ${
            isEdit ? 'editada com sucesso' : 'adicionada com sucesso'
          }`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('dashboards');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erros ao criar URL`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao ${isEdit ? 'editar' : 'adicionar'} URL`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleRemoveSelectedItem = (itemsId: string) => {
    setClientesSelecionados(
      clientesSelecionados.filter(item => item.value !== itemsId),
    );
  };

  function handleNomeRelatorio(e: any) {
    setNomeRelatorio(e.target.value);
    clearErrors('nomeRelatorio');
  }
  function handleUrlApi(e: any) {
    setUrlApi(e.target.value);
    clearErrors('urlApi');
  }
  function handleNomeTabelaCliente(e: any) {
    setNomeTabelaCliente(e.target.value);
    clearErrors('nomeTabelaCliente');
  }
  function handleCampoCodigoCliente(e: any) {
    setCampoCodigoCliente(e.target.value);
    clearErrors('campoCodigoCliente');
  }
  function handleNomeTabelaGrupoEconomico(e: any) {
    setNomeTabelaGrupoEconomico(e.target.value);
    clearErrors('nomeTabelaGrupoEconomico');
    if (
      e.target.value.trim().length ||
      (!!campoCodigoGrupoEconomico && campoCodigoGrupoEconomico.trim().length)
    ) {
      setNomeCampoGrupoEconomicoObrigatorio(true);
    } else {
      setNomeCampoGrupoEconomicoObrigatorio(false);
      clearErrors('campoCodigoGrupoEconomico');
    }
  }
  function handleCampoCodigoGrupoEconomico(e: any) {
    setCampoCodigoGrupoEconomico(e.target.value);
    clearErrors('campoCodigoGrupoEconomico');
    if (
      e.target.value.trim().length ||
      (!!nomeTabelaGrupoEconomico && nomeTabelaGrupoEconomico.trim().length)
    ) {
      setNomeCampoGrupoEconomicoObrigatorio(true);
    } else {
      setNomeCampoGrupoEconomicoObrigatorio(false);
      clearErrors('nomeTabelaGrupoEconomico');
    }
  }
  const handleSelectedClientsChange = async (itens?: SelectItem[]) => {
    setClientesSelecionados(itens || []);
  };
  function handleTenantId(e: any) {
    setTenantId(e.target.value);
    clearErrors('tenantId');
  }
  function handleClientId(e: any) {
    setClientId(e.target.value);
    clearErrors('clientId');
  }
  function handleClientSecret(e: any) {
    setClientSecret(e.target.value);
    clearErrors('clientSecret');
  }

  return (
    <>
      {!isEdit && (
        <Button
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          onClick={handleOpen}
        >
          Cadastrar URL
        </Button>
      )}
      {isEdit && (
        <Tooltip hasArrow label="Editar dashboard" bg="black">
          <IconButton
            mr={3}
            size="sm"
            aria-label="Editar dashboard"
            icon={<Icon as={FiEdit} />}
            onClick={handleOpen}
          />
        </Tooltip>
      )}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>{isEdit ? 'Editar URL' : 'Cadastrar URL'}</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={2}>
              <Stack>
                <FormControl isInvalid={errors.nomeRelatorio}>
                  <Input
                    type="text"
                    label="Nome do Relatório"
                    isRequired
                    error={errors.nomeRelatorio}
                    fontSize="15px"
                    value={nomeRelatorio}
                    name="nomeRelatorio"
                    maxLength={60}
                    onChange={e => handleNomeRelatorio(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </Stack>
              <Stack>
                <FormControl isInvalid={errors.urlApi}>
                  <Input
                    type="text"
                    label="URL da API"
                    isRequired
                    error={errors.urlApi}
                    fontSize="15px"
                    value={urlApi}
                    name="urlApi"
                    maxLength={255}
                    onChange={e => handleUrlApi(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </Stack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.nomeTabelaCliente}>
                  <FormLabel
                    htmlFor="nomeTabelaCliente"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                  >
                    Nome da Tabela Cliente
                    <Text color="red" paddingLeft="5px" paddingRight="5px">
                      *
                    </Text>
                    <Tooltip
                      hasArrow
                      label="Indicar a tabela que será utilizada para a filtragem pelo usuário no dashboard"
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="text"
                    isRequired
                    error={errors.nomeTabelaCliente}
                    fontSize="15px"
                    maxLength={255}
                    value={nomeTabelaCliente}
                    name="nomeTabelaCliente"
                    onChange={e => handleNomeTabelaCliente(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.campoCodigoCliente}>
                  <FormLabel
                    htmlFor="campoCodigoCliente"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                  >
                    Campo do Código Cliente
                    <Text color="red" paddingLeft="5px" paddingRight="5px">
                      *
                    </Text>
                    <Tooltip
                      hasArrow
                      label="Identificar dentro da tabela cliente qual o campo correspondente ao código do cliente"
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="text"
                    isRequired
                    error={errors.campoCodigoCliente}
                    fontSize="15px"
                    maxLength={255}
                    value={campoCodigoCliente}
                    name="campoCodigoCliente"
                    onChange={e => handleCampoCodigoCliente(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </HStack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.nomeTabelaGrupoEconomico}>
                  <FormLabel
                    htmlFor="nomeTabelaGrupoEconomico"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                    gridGap={1}
                  >
                    Nome da Tabela Grupo Econômico
                    {nomeCampoGrupoEconomicoObrigatorio && (
                      <Text color="red" paddingLeft="5px" paddingRight="5px">
                        *
                      </Text>
                    )}
                    <Tooltip
                      ml={1}
                      hasArrow
                      label="Indicar a tabela que será utilizada para a filtragem do grupo econômico no dashboard"
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="text"
                    error={errors.nomeTabelaGrupoEconomico}
                    fontSize="15px"
                    maxLength={255}
                    value={nomeTabelaGrupoEconomico}
                    name="nomeTabelaGrupoEconomico"
                    onChange={e => handleNomeTabelaGrupoEconomico(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.campoCodigoGrupoEconomico}>
                  <FormLabel
                    htmlFor="campoCodigoGrupoEconomico"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                    gridGap={1}
                  >
                    Campo do Código Grupo Econômico
                    {nomeCampoGrupoEconomicoObrigatorio && (
                      <Text color="red" paddingLeft="5px" paddingRight="5px">
                        *
                      </Text>
                    )}
                    <Tooltip
                      ml={1}
                      hasArrow
                      label="Identificar dentro da tabela grupo econômico qual o campo correspondente ao código do grupo econômico"
                      bg="gray"
                      shouldWrapChildren
                    >
                      <Icon as={BsFillInfoCircleFill} />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="text"
                    error={errors.campoCodigoGrupoEconomico}
                    fontSize="15px"
                    maxLength={255}
                    value={campoCodigoGrupoEconomico}
                    name="campoCodigoGrupoEconomico"
                    onChange={e => handleCampoCodigoGrupoEconomico(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </HStack>
              <HStack alignItems="baseline">
                <FormControl isInvalid={errors.tenantId}>
                  <FormLabel
                    htmlFor="tenantId"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                    gridGap={1}
                  >
                    TENANT_ID
                    <Text color="red" paddingLeft="5px" paddingRight="5px">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    type="text"
                    error={errors.tenantId}
                    fontSize="15px"
                    maxLength={255}
                    value={tenantId}
                    name="tenantId"
                    onChange={e => handleTenantId(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.clientId}>
                  <FormLabel
                    htmlFor="clientId"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                    gridGap={1}
                  >
                    CLIENT_ID
                    <Text color="red" paddingLeft="5px" paddingRight="5px">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    type="text"
                    error={errors.clientId}
                    fontSize="15px"
                    maxLength={255}
                    value={clientId}
                    name="clientId"
                    onChange={e => handleClientId(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
                <FormControl isInvalid={errors.clientSecret}>
                  <FormLabel
                    htmlFor="clientSecret"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                    gridGap={1}
                  >
                    CLIENT_SECRET
                    <Text color="red" paddingLeft="5px" paddingRight="5px">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    type="text"
                    error={errors.clientSecret}
                    fontSize="15px"
                    maxLength={255}
                    value={clientSecret}
                    name="clientSecret"
                    onChange={e => handleClientSecret(e)}
                    isDisabled={isFetching}
                  />
                </FormControl>
              </HStack>
              <Stack>
                <FormControl>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="initial"
                    display="flex"
                  >
                    Configurar acesso
                  </FormLabel>
                  <Text style={{ fontSize: '14px' }}>
                    Informe os clientes que{' '}
                    <span style={{ fontWeight: 'bold' }}>DEVEM</span> acessar ao
                    relatório.
                  </Text>
                </FormControl>
              </Stack>
              <Stack>
                <MultiSelect
                  value={clientesSelecionados}
                  options={clientes}
                  onChange={(changes: SelectItem[]) =>
                    handleSelectedClientsChange(changes)
                  }
                  disabled={isFetching}
                  labelledBy="Selecioner cliente"
                  overrideStrings={MultiSelectStrings}
                />
              </Stack>
              <Stack overflowX="auto" maxHeight="300px">
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th p="0.5rem !important">Cliente</Th>
                      <Th p="0.5rem !important" w="15%">
                        Ação
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {clientesSelecionados?.length > 0 ? (
                      clientesSelecionados?.map(item => (
                        <Tr key={item.value}>
                          <Td
                            fontSize="15px"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            maxWidth="200px"
                            p="0.5rem !important"
                            style={{ borderColor: '#e5e5e6' }}
                          >
                            <Tooltip label={item.label} placement="top-start">
                              {item.label}
                            </Tooltip>
                          </Td>
                          <Td
                            p="0.5rem !important"
                            style={{ borderColor: '#e5e5e6' }}
                          >
                            <Tooltip label="Remover" placement="top">
                              <IconButton
                                w="25px"
                                isDisabled={isFetching}
                                fontSize="20px"
                                background="transparent"
                                aria-label="Remove item"
                                icon={<Icon as={FiTrash} />}
                                onClick={() =>
                                  handleRemoveSelectedItem(item.value)
                                }
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td align="center" valign="middle" h="100%" colSpan={4}>
                          Não há dados a serem apresentados
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Stack>
            </Stack>
            <Flex justifyContent="end" mt={2}>
              <Button
                type="submit"
                onClick={handleClose}
                color="black"
                background="transparent"
                style={{
                  marginTop: '20px',
                  marginRight: '10px',
                }}
                isDisabled={isFetching}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit(handleCreate)}
                color="white"
                background="gray.900"
                _hover={{
                  bgColor: 'black',
                }}
                style={{ marginTop: '20px' }}
                isDisabled={isFetching}
              >
                Salvar Alterações{' '}
                {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
